'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import {LayoutGroup, motion} from 'framer-motion';
import React, {forwardRef, PropsWithChildren, Ref, useId} from 'react';
import {TouchTarget} from './button';
import {Link} from './link';
import {WithOptionalClassName} from 'utils/typeUtils';

export function Navbar({
  className,
  ...props
}: WithOptionalClassName<React.HTMLAttributes<HTMLElement>>) {
  return (
    <nav
      {...props}
      className={clsx(className, 'flex flex-1 items-center gap-4 py-2.5')}
    />
  );
}

export function NavbarDivider({
  className,
  ...props
}: WithOptionalClassName<React.HTMLAttributes<HTMLElement>>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, 'h-6 w-px bg-zinc-950/10 dark:bg-white/10')}
    />
  );
}

export function NavbarSection({
  className,
  ...props
}: WithOptionalClassName<React.HTMLAttributes<HTMLDivElement>>) {
  let id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, 'flex items-center gap-3')} />
    </LayoutGroup>
  );
}

export function NavbarSpacer({
  className,
  ...props
}: WithOptionalClassName<React.HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, '-ml-4 flex-1')}
    />
  );
}

type NavbarItemProps = PropsWithChildren<WithOptionalClassName<{current?: string; variant?: 'default' | 'light'}>> & (
  | ({ href: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'as' | 'className' | 'href'>)
  | ({ href?: never } & Omit<Headless.ButtonProps, 'as' | 'className'>)
);

export const NavbarItem = forwardRef(function NavbarItem(
  {current, className, children, variant = 'default', ...props}: NavbarItemProps,
  ref,
) {
  const isLight = variant === 'light';

  let classes = clsx(
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium sm:text-sm/5',
    // Text color based on variant
    isLight ? 'text-white/80 hover:text-white' : 'text-zinc-950',
    // Leading icon/icon-only
    'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 sm:data-[slot=icon]:*:size-5',
    isLight ? 'data-[slot=icon]:*:fill-white/80' : 'data-[slot=icon]:*:fill-zinc-500',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:[&:not(:nth-child(2))]:*:ml-auto data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-5 sm:data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--avatar-radius:theme(borderRadius.DEFAULT)] data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
    // Hover
    isLight ? 'data-[hover]:bg-white/10 data-[slot=icon]:*:data-[hover]:fill-white' : 'data-[hover]:bg-zinc-950/5 data-[slot=icon]:*:data-[hover]:fill-zinc-950',
    // Active
    isLight ? 'data-[active]:bg-white/10 data-[slot=icon]:*:data-[active]:fill-white' : 'data-[active]:bg-zinc-950/5 data-[slot=icon]:*:data-[active]:fill-zinc-950',
    // Dark mode (only for default variant)
    !isLight && 'dark:text-white dark:data-[slot=icon]:*:fill-zinc-400',
    !isLight && 'dark:data-[hover]:bg-white/5 dark:data-[slot=icon]:*:data-[hover]:fill-white',
    !isLight && 'dark:data-[active]:bg-white/5 dark:data-[slot=icon]:*:data-[active]:fill-white',
  );

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className={clsx(
            "absolute inset-x-2 bottom-0.5 h-0.5 rounded-full",
            isLight ? "bg-white" : "bg-zinc-950 dark:bg-white"
          )}
        />
      )}
      {'href' in props ? (
        <Link
          {...(props as Omit<React.ComponentPropsWithoutRef<typeof Link>, 'as' | 'className' | 'href'> & { href: string })}
          href={props.href as string}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref as Ref<HTMLAnchorElement>}>
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...(props as Omit<Headless.ButtonProps, 'as' | 'className'>)}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref as Ref<HTMLButtonElement>}>
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

export function NavbarLabel({className, ...props}: WithOptionalClassName<React.HTMLAttributes<HTMLSpanElement>>) {
  return <span {...props} className={clsx(className, 'truncate')} />;
}
